import {
  Component,
  HostListener,
  Inject,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core'
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms'
import { AsyncPipe } from '@angular/common'
import { Router } from '@angular/router'

import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
} from '@angular/material/dialog'
import { MatAutocompleteModule } from '@angular/material/autocomplete'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Observable, of } from 'rxjs'
import { debounceTime, startWith, switchMap } from 'rxjs/operators'
import { MeetingRecordingService } from '../../services/meeting-recording.service'
import { AudioWaveformComponent } from '../../components/audio-waveform/audio-waveform.component'
import {
  SelectComponent,
  Option,
} from '../../components/select/select.component'
import { AssessmentStore } from '../../stores/assessment.store'
import { UserStore } from '../../stores/user.store'
import { isFeatureAllowed } from '../../config/feature-permission.config'
import { SkillMap } from '../../services/assessment/assessment.mapping'
import { resolveInitialSkillMap } from '../../services/assessment/skill-map-initial-selection'
import {
  DefaultSkillMapTarget,
  StartRecordingSessionGQL,
  FinalizeRecordingGQL,
} from '../../../_generated/graphql'

/**
 * phaseIds が空配列でないことを検証するカスタムバリデータ
 */
function phaseIdsRequiredValidator(
  control: AbstractControl,
): ValidationErrors | null {
  const value = control.value
  if (!Array.isArray(value) || value.length === 0) {
    return { required: true }
  }
  return null
}

/** 商談録音画面コンポーネント */
@UntilDestroy()
@Component({
  selector: 'app-meeting-recording-page',
  standalone: true,
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    MatDialogModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatInputModule,
    MatSnackBarModule,
    AudioWaveformComponent,
    SelectComponent,
  ],
  templateUrl: './meeting-recording-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./meeting-recording-page.component.scss'],
})
export class MeetingRecordingPageComponent implements OnInit, OnDestroy {
  /** 録音設定フォーム */
  form!: FormGroup

  /** 顧客名 FormControl（オートコンプリート用） */
  accountName = new FormControl('')

  /** マイク許可エラーメッセージ */
  micError = ''

  /** 録音中にバックグラウンド遷移した際の警告メッセージ */
  backgroundWarning = ''

  /** 録音中のMediaStream */
  mediaStream: MediaStream | null = null

  /** 現在の録音状態 */
  isRecording = false

  /** 経過秒数 */
  elapsedSeconds = 0

  /** スキルマップ一覧（API取得） */
  skillMaps: SkillMap[] = []

  /** スキルマップ ドロップダウン用 Option 配列 */
  skillMapOptions: Option[] = []

  /** 選択中のスキルマップ */
  selectedSkillMap: Option = { id: '', name: '' }

  /** スキルマップが0件で録音不可な場合のエラーメッセージ */
  skillMapError = ''

  /** 顧客名オートコンプリート候補 */
  accounts: { name: string }[] = []

  /** フィルタ済み顧客名候補 */
  filteredAccounts: Observable<{ name: string }[]> = of([])

  /** アップロード進捗 */
  uploadProgress$: Observable<{ uploaded: number; total: number }> | null = null

  /** 録音セッションID */
  private sessionId = ''

  /** 90秒未満で終了した場合にfinalizeを呼ばないためのフラグ */
  private shouldFinalize = true

  /** 録音開始日時 */
  private recordStartDate: Date | null = null

  constructor(
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar,
    private readonly recordingService: MeetingRecordingService,
    private readonly assessmentStore: AssessmentStore,
    private readonly userStore: UserStore,
    private readonly startRecordingSessionGQL: StartRecordingSessionGQL,
    private readonly finalizeRecordingGQL: FinalizeRecordingGQL,
  ) {}

  /** 経過時間をMM:SS形式で返す */
  get formattedElapsedTime(): string {
    const minutes = Math.floor(this.elapsedSeconds / 60)
    const seconds = this.elapsedSeconds % 60
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
  }

  /** 録音中にページ離脱を防止する */
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.isRecording) {
      event.preventDefault()
      event.returnValue = ''
    }
  }

  /** 録音中にバックグラウンド遷移した場合に警告を表示する（モバイル想定） */
  @HostListener('document:visibilitychange')
  onVisibilityChange(): void {
    if (document.visibilityState === 'visible') {
      this.backgroundWarning = ''
      this.recordingService.onVisibilityRestored()
      return
    }
    if (this.isRecording) {
      this.backgroundWarning =
        '画面がバックグラウンドに移行しました。ブラウザによっては録音が中断される可能性があります。'
    }
  }

  /** 録音中はSPA内遷移を確認ダイアログで防止する */
  canDeactivate(): boolean | Observable<boolean> {
    if (!this.isRecording) {
      return true
    }
    return new Observable<boolean>((observer) => {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: { message: '録音中です。録音を中止してページを離れますか？' },
      })
      dialogRef.afterClosed().subscribe((confirmed) => {
        if (confirmed) {
          this.shouldFinalize = false
          this.recordingService.stopRecording()
        }
        observer.next(!!confirmed)
        observer.complete()
      })
    })
  }

  ngOnInit(): void {
    this.recordingService.reset()
    this.shouldFinalize = true

    this.form = new FormGroup({
      title: new FormControl(
        this.buildDefaultTitle(new Date()),
        Validators.required,
      ),
      skillMapId: new FormControl('', Validators.required),
      phaseIds: new FormControl([] as string[], phaseIdsRequiredValidator),
    })

    this.uploadProgress$ = this.recordingService.uploadProgress$

    this.assessmentStore
      .getSkillMaps()
      .pipe(untilDestroyed(this))
      .subscribe((skillMaps) => {
        this.skillMaps = skillMaps
        this.skillMapOptions = skillMaps.map((sm) => ({
          id: sm.id,
          name: sm.name,
        }))
        if (skillMaps.length === 0) {
          this.skillMapError =
            'スキルマップが存在しません。管理者にお問い合わせください。'
          return
        }
        this.skillMapError = ''
        const initial = resolveInitialSkillMap(
          skillMaps,
          DefaultSkillMapTarget.MeetingRecording,
        )
        if (initial) {
          this.changeSkillMap({ id: initial.id, name: initial.name })
        }
      })

    // 顧客名一覧を取得
    this.assessmentStore
      .getAccounts()
      .pipe(untilDestroyed(this))
      .subscribe((accounts) => {
        this.accounts = accounts
      })

    // 顧客名オートコンプリートのフィルタ
    this.filteredAccounts = this.accountName.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      switchMap((val) => {
        const lowerCaseVal = (val ?? '').toLowerCase()
        return of(
          this.accounts.filter((account) =>
            account.name.toLowerCase().includes(lowerCaseVal),
          ),
        )
      }),
    )

    // 録音状態の監視
    this.recordingService.state$
      .pipe(untilDestroyed(this))
      .subscribe((state) => {
        this.isRecording = state === 'recording' || state === 'finalizing'
        if (state === 'done') {
          if (this.shouldFinalize) {
            if (this.recordingService.hasUploadErrors) {
              this.recordingService.cleanupUploadedChunks().finally(() => {
                this.snackBar.open(
                  '一部のチャンクのアップロードに失敗しました。録音をやり直してください。',
                  '閉じる',
                  {
                    duration: 5000,
                  },
                )
                this.router.navigate(['/assessments'])
              })
            } else {
              this.callFinalizeRecording()
            }
          } else {
            this.recordingService.cleanupUploadedChunks().finally(() => {
              this.router.navigate(['/assessments'])
            })
          }
        }
      })

    // 経過時間の監視
    this.recordingService.elapsedSeconds$
      .pipe(untilDestroyed(this))
      .subscribe((seconds) => {
        this.elapsedSeconds = seconds
      })
  }

  ngOnDestroy(): void {
    this.stopMediaStream()
    if (!this.shouldFinalize && this.sessionId) {
      this.recordingService.cleanupUploadedChunks()
    }
  }

  /** MediaStream のすべてのトラックを停止してストリームを解放する */
  private stopMediaStream(): void {
    this.mediaStream?.getTracks().forEach((track) => track.stop())
    this.mediaStream = null
  }

  /** SKIP_AI_EVALUATION 有効テナントか（スキルマップ選択なし・AI評価なしで録音する） */
  get isEvaluationSkipped(): boolean {
    return isFeatureAllowed(
      'SKIP_AI_EVALUATION',
      this.userStore.ctxUser()?.enabledFeatures,
    )
  }

  /** フォームが有効で録音開始可能か */
  get canStartRecording(): boolean {
    // skip 時はスキルマップ・フェーズを送らないため商談名のみ必須
    if (this.isEvaluationSkipped) {
      return this.form.controls['title'].valid && !this.isRecording
    }
    return this.form.valid && !this.isRecording
  }

  /**
   * スキルマップ選択変更。
   * 該当フェーズはスキルマップに対して 1:1 を前提とし、選択されたスキルマップが
   * 持つ全フェーズを phaseIds に自動的に反映する。
   */
  changeSkillMap(option: Option): void {
    const skillMap = this.skillMaps.find((sm) => sm.id === option.id)
    if (!skillMap) {
      throw new Error(`SkillMap not found: ${option.id}`)
    }
    this.selectedSkillMap = option
    this.form.patchValue({
      skillMapId: option.id,
      phaseIds: skillMap.phases.map((phase) => phase.id),
    })
  }

  /** デフォルトの商談名を「商談録音 - YYYY/MM/DD hh:mm」形式で生成する */
  private buildDefaultTitle(date: Date): string {
    const yyyy = String(date.getFullYear())
    const mm = String(date.getMonth() + 1).padStart(2, '0')
    const dd = String(date.getDate()).padStart(2, '0')
    const hh = String(date.getHours()).padStart(2, '0')
    const mi = String(date.getMinutes()).padStart(2, '0')
    return `商談録音 - ${yyyy}/${mm}/${dd} ${hh}:${mi}`
  }

  /** 録音開始 */
  async onStartRecording(): Promise<void> {
    this.micError = ''
    this.backgroundWarning = ''
    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      })

      this.startRecordingSessionGQL
        .mutate({
          variables: {
            input: {
              title: this.form.value.title,
              // skip 時は SkillMap 未選択（=AI評価なし）として送る
              skillMapId: this.isEvaluationSkipped
                ? null
                : this.form.value.skillMapId,
              phaseIds: this.isEvaluationSkipped
                ? []
                : this.form.value.phaseIds,
            },
          },
        })
        .pipe(untilDestroyed(this))
        .subscribe({
          next: (result) => {
            const sessionId = result.data?.startRecordingSession.sessionId
            if (!sessionId) {
              this.stopMediaStream()
              this.micError = 'セッションの開始に失敗しました。'
              return
            }
            this.sessionId = sessionId
            this.recordStartDate = new Date()
            const orgId = this.userStore.ctxUser()?.orgId
            if (!orgId) {
              this.stopMediaStream()
              this.micError = 'セッションの開始に失敗しました。'
              return
            }
            try {
              this.recordingService.startRecording(
                this.mediaStream!,
                this.sessionId,
                orgId,
              )
            } catch (error) {
              this.stopMediaStream()
              this.micError =
                error instanceof Error &&
                error.message === 'No supported audio MIME type found'
                  ? 'このブラウザでは録音機能に対応していません。Chrome を使用してください。'
                  : '録音の開始に失敗しました。'
            }
          },
          error: () => {
            this.micError = '録音セッションの開始に失敗しました。'
            this.stopMediaStream()
          },
        })
    } catch {
      this.micError =
        'マイクへのアクセスが拒否されました。ブラウザの設定を確認してください。'
    }
  }

  /** 録音停止 */
  onStopRecording(): void {
    if (this.elapsedSeconds < 90) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: {
          message:
            '録音時間が90秒未満のためアセスメントは作成されません。録音を終了しますか？',
        },
      })
      dialogRef.afterClosed().subscribe((confirmed) => {
        if (confirmed) {
          this.shouldFinalize = false
          this.recordingService.stopRecording()
        }
      })
    } else {
      this.recordingService.stopRecording()
    }
  }

  /** 録音完了後にアセスメントを作成する */
  private callFinalizeRecording(): void {
    if (!this.recordStartDate) {
      throw new Error('recordStartDate is not set')
    }
    this.finalizeRecordingGQL
      .mutate({
        variables: {
          input: {
            sessionId: this.sessionId,
            title: this.form.value.title,
            accountName: this.accountName.value ?? undefined,
            recordDate: this.recordStartDate,
            // skip 時は SkillMap 未選択（=AI評価なし）として送る
            skillMapId: this.isEvaluationSkipped
              ? null
              : this.form.value.skillMapId,
            phaseIds: this.isEvaluationSkipped ? [] : this.form.value.phaseIds,
            durationSeconds: this.elapsedSeconds,
            totalChunks: this.recordingService.totalChunks,
            mimeType: this.recordingService.currentMimeType,
            chunkBucketName: this.recordingService.chunkBucketName,
          },
        },
      })
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.snackBar.open(
            this.isEvaluationSkipped
              ? '録音が完了しました。文字起こしを開始します。'
              : '録音が完了しました。評価を開始します。',
            '閉じる',
            {
              duration: 5000,
            },
          )
          this.router.navigate(['/assessments'])
        },
        error: (err) => {
          console.error('finalizeRecording failed:', err)
          this.snackBar.open('録音の確定に失敗しました。', '閉じる', {
            duration: 5000,
          })
          this.router.navigate(['/assessments'])
        },
      })
  }
}

/** 確認ダイアログコンポーネント（インライン定義） */
@Component({
  selector: 'app-confirm-dialog',
  standalone: true,
  imports: [MatDialogModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <h2 mat-dialog-title>確認</h2>
    <mat-dialog-content>{{ data.message }}</mat-dialog-content>
    <mat-dialog-actions>
      <button mat-button [mat-dialog-close]="false">キャンセル</button>
      <button mat-button [mat-dialog-close]="true" color="primary">終了</button>
    </mat-dialog-actions>
  `,
})
export class ConfirmDialogComponent {
  constructor(@Inject(MAT_DIALOG_DATA) public data: { message: string }) {}
}
